import * as React from "react";
import {
  useNavigation,
  useOutletContext,
  useRevalidator,
  useSearchParams,
} from "react-router-dom";
import { AccountHolder, useAccount } from "../../../Account";
import {
  Alert,
  Box,
  Button,
  Divider,
  SelectChangeEvent,
  TablePagination,
} from "@mui/material";
import { useLanguage } from "../../../Language";
import { HostSystemItemSortType } from "../../../../dto/HostSystemItemSortType";
import { useGlobalConfig } from "../../../GlobalConfig";
import { UpdateDigitalLoansButton } from "../UpdateDigitalLoansButton";
import { HoldsSortControl } from "../HostSystemItemSortControl";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";
import { useCallback, useEffect, useMemo } from "react";
import { CancelHoldButton } from "../CancelHoldButton";
import { SuspendHoldsDialog } from "./SuspendHoldsDialog";
import {
  canCancelHold,
  canReactivateOrSuspendHold,
  HoldsRowItem,
} from "./HoldsRowItem";
import { LSResultItemSkeleton } from "../../search/ls/LSResultItem/LSResultItemSkeleton";
import AccountPrintHoldsDialog from "./AccountPrintHoldsDialog";
import { SuspendHoldInfo } from "../../../services/Holds";
import { useToast } from "@tlc/ui/toast";
import { useHoldsSelection } from "./HoldsSelectionContext";
import { secondaryTypes, SharedViewHeader } from "../SharedViewHeader";
import { useAnalytics } from "../../../../analytics/useGoogleAnalytics";
import { HoldsBatchErrorDialog } from "./HoldsBatchErrorDialog";
import { useDialogRegistry } from "@tlc/ui/dialog/DialogRegistry";

function getKey(bibliographicId: string, holdOrDesireNumber: string | number) {
  // holdNumber/desireNumber is always 1 for eBooks
  // bibliographicId may be duplicated, (multiple item level holds on the same title, etc.)
  // key is constructed using properties depended on by cancel, suspend, unsuspend API calls
  return `${bibliographicId}_${holdOrDesireNumber}`;
}

export const getKeyForHold = ({
  bibliographicId,
  holdNumber,
}: Pick<HostSystemItemView, "bibliographicId" | "holdNumber">): string => {
  return getKey(bibliographicId, holdNumber);
};

export const getKeyForSuspendHoldInfo = ({
  bibliographicId,
  desireNumber,
}: Pick<SuspendHoldInfo, "bibliographicId" | "desireNumber">) => {
  return getKey(bibliographicId, desireNumber);
};

export interface HoldsSharedViewProps {
  page: number;
  pageSize: number;
  sort: HostSystemItemSortType;
  holds: HostSystemItemView[];
  totalHolds: number;
  totalArrivedHolds: number;
  fetchPrintHolds?: () => Promise<HostSystemItemView[]>;
  patronId: string;
  digitalInformationLastUpdated?: number;
  isStudent: boolean;
  cancelHoldsApi: (
    holds: HostSystemItemView[],
  ) => Promise<{ cancelHoldInfos: any[] }>;

  unsuspendHoldsApi: (
    holds: HostSystemItemView[],
  ) => Promise<{ suspendHoldInfos: any[] }>;
}

export interface FailedBatchAction {
  actionType: "cancel" | "reactivate";
  failedHolds: HostSystemItemView[];
  successCount: number;
}

export const HoldsSharedView: React.FC<HoldsSharedViewProps> = ({
  page,
  pageSize,
  sort,
  holds,
  totalHolds,
  totalArrivedHolds,
  fetchPrintHolds,
  patronId,
  digitalInformationLastUpdated,
  isStudent,
  cancelHoldsApi,
  unsuspendHoldsApi,
}) => {
  const revalidator = useRevalidator();
  const navigation = useNavigation();
  const { translate } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const { allowBorrowersToCancelArrivedHolds } = useGlobalConfig();
  const { showSuccessToast, showToast } = useToast();
  const { refreshAccountData } = useAccount();
  const { scrollToTop } = useOutletContext<{ scrollToTop: () => void }>();
  const [holdsToSuspend, setHoldsToSuspend] = React.useState<
    HostSystemItemView[]
  >([]);
  const [failedBatchAction, setFailedBatchAction] =
    React.useState<FailedBatchAction | null>(null);
  const loading =
    navigation.state === "loading" || navigation.state === "submitting";

  const {
    selected,
    selectedLength,
    isAllSelected,
    isAllIndeterminate,
    handleItemChange,
    handleSelectAllChange,
  } = useHoldsSelection();

  const { trackCancelHold, trackReactivateHold } = useAnalytics();

  // get hold objects from selected ids
  const selectedObjects = useMemo(
    () => holds.filter((hold) => selected.includes(getKeyForHold(hold))),
    [holds, selected],
  );

  const onClickSuspendHolds = () => {
    const suspendableHolds = selectedObjects.filter((hold) =>
      canReactivateOrSuspendHold(hold),
    );
    setHoldsToSuspend(suspendableHolds);
  };

  const onClickCancelHolds = async () => {
    const cancellableHolds = selectedObjects.filter((hold) =>
      canCancelHold(hold, allowBorrowersToCancelArrivedHolds),
    );

    if (cancellableHolds.length === 0) return;
    try {
      const { cancelHoldInfos } = await cancelHoldsApi(cancellableHolds);
      trackCancelHold(cancelHoldInfos.length);

      const successes = cancelHoldInfos.filter((info) => info.success);
      const errors = cancelHoldInfos.filter((info) => !info.success);

      // All errors - show error toast
      if (successes.length < 1 && errors.length > 0) {
        showToast(
          `${errors.length} ${translate("cancelHoldFailureMessage")}`,
          "error",
        );

        return;
      }

      // Has errors and successes - show errors dialog, success toast when dialog is closed
      if (successes.length > 0 && errors.length > 0) {
        const errorKeys = errors.map(getKeyForSuspendHoldInfo);
        const errorHolds = holds.filter((hold) =>
          errorKeys.includes(getKeyForHold(hold)),
        );
        setFailedBatchAction({
          actionType: "cancel",
          failedHolds: errorHolds,
          successCount: successes.length,
        });
        return;
      }

      // All successful - show success toast
      if (successes.length > 0 && errors.length < 1) {
        showSuccessToast(`${successes.length} ${translate("canceledMessage")}`);
        refreshAccountData();

        const successKeys = successes.map(getKeyForSuspendHoldInfo);
        const successHolds = holds.filter((hold) =>
          successKeys.includes(getKeyForHold(hold)),
        );
        successHolds.forEach((hold) => handleItemChange(hold, false));
        refreshPage();
        return;
      }
    } catch (e) {
      showToast(
        `${cancellableHolds.length} ${translate("cancelHoldFailureMessage")}`,
        "error",
      );
    }
  };

  const onClickReactivateHolds = async () => {
    const reactivatableHolds = selectedObjects.filter(
      (hold) => canReactivateOrSuspendHold(hold) && !!hold.suspendUntilDate,
    );

    if (reactivatableHolds.length === 0) return;
    try {
      const { suspendHoldInfos } = await unsuspendHoldsApi(reactivatableHolds);
      trackReactivateHold(suspendHoldInfos.length);

      const successes = suspendHoldInfos.filter((info) => info.success);
      const errors = suspendHoldInfos.filter((info) => !info.success);

      // All errors - show error toast
      if (successes.length < 1 && errors.length > 0) {
        showToast(
          `${errors.length} ${translate("reactivateHoldsErrorMessage")}`,
          "error",
        );

        return;
      }

      // Has errors and successes - show errors dialog, success toast when dialog is closed
      if (errors.length > 0) {
        const errorKeys = errors.map(getKeyForSuspendHoldInfo);
        const errorHolds = holds.filter((hold) =>
          errorKeys.includes(getKeyForHold(hold)),
        );
        setFailedBatchAction({
          actionType: "reactivate",
          failedHolds: errorHolds,
          successCount: successes.length,
        });
      }

      // All successful - show success toast
      if (errors.length < 1 && successes.length > 0) {
        showSuccessToast(
          `${successes.length} ${translate("unsuspendMessage")}`,
        );

        const successKeys = successes.map(getKeyForSuspendHoldInfo);
        const successHolds = holds.filter((hold) =>
          successKeys.includes(getKeyForHold(hold)),
        );
        successHolds.forEach((hold) => handleItemChange(hold, false));

        refreshPage();
      }
    } catch (e) {
      showToast(
        `${reactivatableHolds.length} ${translate(
          "reactivateHoldsErrorMessage",
        )}`,
        "error",
      );
    }
  };

  const refreshPage = async () => {
    revalidator.revalidate();
  };

  const handleSortChange = async (event: SelectChangeEvent) => {
    const newSort = event.target.value as HostSystemItemSortType;
    searchParams.set("page", "0");
    searchParams.set("sort", newSort);
    setSearchParams(searchParams);
  };

  const handleChangePage = async (event: unknown, newPage: number) => {
    searchParams.set("page", newPage.toString());
    setSearchParams(searchParams);
    scrollToTop();
  };

  const handleChangeRowsPerPage = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    searchParams.set("page", "0");
    searchParams.set("size", event.target.value);
    setSearchParams(searchParams);
  };

  const reactivateDisabled = React.useMemo(() => {
    if (loading || selectedLength === 0) return true;
    // disable if NONE of the selected holds have suspend dates
    return !selectedObjects.some((value) => !!value.suspendUntilDate);
  }, [selectedObjects, loading, selectedLength]);

  const suspendDisabled = React.useMemo(() => {
    if (loading || selectedLength === 0) return true;

    // disable if every hold is arrived or in transit
    return selectedObjects.every((hold) => !canReactivateOrSuspendHold(hold));
  }, [selectedObjects, loading, selectedLength]);

  const cancelDisabled = React.useMemo(() => {
    if (loading || selectedLength === 0) return true;
    // disable if every hold is in transit, or arrived and allowBorrowersToCancelArrivedHolds is also disabled
    return selectedObjects.every(
      (hold) => !canCancelHold(hold, allowBorrowersToCancelArrivedHolds),
    );
  }, [
    selectedObjects,
    loading,
    selectedLength,
    allowBorrowersToCancelArrivedHolds,
  ]);

  const onSuspend = (suspended: SuspendHoldInfo[]) => {
    const suspendedKeys = suspended.map(getKeyForSuspendHoldInfo);
    const suspendedHolds = holds.filter((hold) =>
      suspendedKeys.includes(getKeyForHold(hold)),
    );
    suspendedHolds.forEach((hold) => {
      handleItemChange(hold, false);
    });

    refreshPage();
  };

  const updateLoansButton = React.useMemo(() => {
    return isStudent ? (
      <UpdateDigitalLoansButton patronId={patronId} disabled={loading} />
    ) : (
      <UpdateDigitalLoansButton
        disabled={loading}
        patronId={patronId}
        onUpdate={refreshAccountData}
        lastUpdated={digitalInformationLastUpdated}
      />
    );
  }, [patronId, digitalInformationLastUpdated, loading]);

  const onCloseHoldsBatchErrorDialog = useCallback((successes: number = 0) => {
    if (successes > 0) {
      showSuccessToast(`${successes} ${translate("unsuspendMessage")}`);
    }
    setFailedBatchAction(null);
    refreshPage();
  }, []);

  const { registerDialog } = useDialogRegistry();
  useEffect(() => {
    return registerDialog(() => {
      setFailedBatchAction(null);
    });
  }, [setFailedBatchAction]);

  const onCloseSuspendDialog = useCallback(() => {
    setHoldsToSuspend([]);
  }, [setHoldsToSuspend]);

  useEffect(() => {
    return registerDialog(onCloseSuspendDialog);
  }, [onCloseSuspendDialog]);

  return (
    <>
      {holds.length === 0 && (
        <Alert severity="info" action={updateLoansButton} sx={{ ml: 1 }}>
          {isStudent
            ? translate("studentNoHoldsMessage")
            : translate("noHoldsMessage")}
        </Alert>
      )}
      {holds.length > 0 && (
        <>
          <SharedViewHeader
            labelKey="requests"
            count={totalHolds}
            secondaryLabelKey="arrived"
            secondaryCount={totalArrivedHolds}
            secondaryType={secondaryTypes.success}
            showSelectAll={isStudent ? false : true}
            selectedLength={selectedLength}
            isAllIndeterminate={isAllIndeterminate}
            isAllSelected={isAllSelected}
            handleSelectAllChange={handleSelectAllChange}
            headerActions={
              <>
                {updateLoansButton}
                {!isStudent && fetchPrintHolds && (
                  <AccountPrintHoldsDialog
                    fetchHolds={fetchPrintHolds}
                    disabled={totalHolds === 0}
                  />
                )}
              </>
            }
            selectedActions={
              <>
                {selectedLength > 0 && (
                  <>
                    <Button
                      disabled={reactivateDisabled}
                      onClick={onClickReactivateHolds}
                    >
                      {translate("reactivate")}
                    </Button>
                    <Button
                      disabled={suspendDisabled}
                      onClick={onClickSuspendHolds}
                    >
                      {translate("suspend")}
                    </Button>
                    <CancelHoldButton
                      disabled={cancelDisabled}
                      onClick={onClickCancelHolds}
                    />
                  </>
                )}
              </>
            }
            sortControl={
              <HoldsSortControl
                disabled={loading}
                value={sort}
                onChange={handleSortChange}
              />
            }
          />
          <Box>
            {loading
              ? Array.from({ length: pageSize }).map((_, index) => (
                  <div key={index}>
                    <LSResultItemSkeleton />
                  </div>
                ))
              : holds.map((row) => {
                  const key = getKeyForHold(row);
                  return (
                    <React.Fragment key={key}>
                      <Divider />
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          ml: isStudent ? 2 : 0,
                        }}
                      >
                        <Box>
                          <HoldsRowItem
                            id={key}
                            hold={row}
                            isStudent={isStudent}
                            onClickCancel={async () => {
                              try {
                                const { cancelHoldInfos } =
                                  await cancelHoldsApi([row]);
                                trackCancelHold(1);
                                if (cancelHoldInfos[0]?.success) {
                                  showSuccessToast(
                                    `1 ${translate("canceledMessage")}`,
                                  );
                                  refreshPage();
                                  await AccountHolder.fetchAccountSummaryAndPatron(
                                    true,
                                  ); // force update of counts
                                } else {
                                  showToast(
                                    `1 ${translate(
                                      "cancelHoldFailureMessage",
                                    )}`,
                                    "error",
                                  );
                                }
                              } catch (e) {
                                showToast(
                                  `1 ${translate("cancelHoldFailureMessage")}`,
                                  "error",
                                );
                              }
                            }}
                            onClickReactivate={async () => {
                              try {
                                const { suspendHoldInfos } =
                                  await unsuspendHoldsApi([row]);
                                trackReactivateHold(1);
                                if (suspendHoldInfos[0]?.success) {
                                  showSuccessToast(
                                    `1 ${translate("unsuspendMessage")}`,
                                  );
                                  refreshPage();
                                } else {
                                  showToast(
                                    `1 ${translate(
                                      "reactivateHoldsErrorMessage",
                                    )}`,
                                    "error",
                                  );
                                }
                              } catch (e) {
                                showToast(
                                  `1 ${translate(
                                    "reactivateHoldsErrorMessage",
                                  )}`,
                                  "error",
                                );
                              }
                            }}
                            onClickSuspend={() => setHoldsToSuspend([row])}
                          />
                        </Box>
                      </Box>
                    </React.Fragment>
                  );
                })}
          </Box>
          <TablePagination
            disabled={loading}
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalHolds}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            slotProps={{
              select: {
                inputProps: { "aria-label": translate("rowsPerPage") },
                native: true,
              },
            }}
          />
        </>
      )}

      <SuspendHoldsDialog
        holds={holdsToSuspend}
        open={holdsToSuspend.length > 0}
        onClose={onCloseSuspendDialog}
        onSuspend={(suspended) => onSuspend(suspended)}
      />

      {failedBatchAction && (
        <HoldsBatchErrorDialog
          failedBatchAction={failedBatchAction}
          onClose={onCloseHoldsBatchErrorDialog}
        />
      )}
    </>
  );
};
